import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { CheckCircle, Brain, Workflow, Users, Puzzle } from 'lucide-react';
const AITraining = () => {
  return <div className="min-h-screen bg-white">
      <Navbar />

      <section className="py-20 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-4xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
              AI Training and Integration
            </h1>
            <p className="text-lg text-gray-700">
              We provide comprehensive AI training services to help your workforce seamlessly integrate with our AI agents.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-2xl font-bold mb-4 text-[#0EA5E9]">Why AI Training Matters</h2>
              <p className="text-gray-700 mb-6">
                Even the most advanced AI systems require proper human guidance to deliver maximum value. Our training programs ensure your teams can effectively collaborate with AI, focusing on:
              </p>
              <ul className="space-y-4">
                {["Prompt engineering and effective AI communication", "Workflow integration between human expertise and AI capabilities", "Quality control and oversight of AI outputs", "Continuous improvement and feedback loops"].map((item, index) => <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#0EA5E9] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-xl">
              <img alt="AI Training Session" className="w-full h-auto rounded-lg" src="https://www.adaptiveus.com/hubfs/RE.jpg" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
              Our Training Approach
            </h2>
            <p className="text-gray-700">
              We offer customized training programs designed to meet the specific needs of your organization and team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[{
            title: "Initial Assessment",
            description: "We evaluate your team's existing AI familiarity and identify the specific skills needed for your use cases.",
            icon: <Brain className="h-10 w-10 text-[#0EA5E9]" />
          }, {
            title: "Customized Curriculum",
            description: "Develop tailored training modules based on your industry, existing workflows, and specific AI implementations.",
            icon: <Puzzle className="h-10 w-10 text-[#0EA5E9]" />
          }, {
            title: "Hands-On Workshops",
            description: "Interactive sessions where your team works directly with the AI tools they'll be using day-to-day.",
            icon: <Users className="h-10 w-10 text-[#0EA5E9]" />
          }, {
            title: "Integration Support",
            description: "Guidance on incorporating AI into existing workflows with minimal disruption and maximum efficiency.",
            icon: <Workflow className="h-10 w-10 text-[#0EA5E9]" />
          }, {
            title: "Ongoing Education",
            description: "Regular updates and additional training as AI capabilities evolve and new features become available.",
            icon: <Brain className="h-10 w-10 text-[#0EA5E9]" />
          }, {
            title: "Certification Program",
            description: "Formal recognition of AI proficiency that acknowledges your team's expertise in human-AI collaboration.",
            icon: <CheckCircle className="h-10 w-10 text-[#0EA5E9]" />
          }].map((item, index) => <Card key={index} className="border border-[#0EA5E9]/20 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.description}</p>
                </CardContent>
              </Card>)}
          </div>

          <div className="bg-[#F0F9FF] p-8 rounded-xl border border-[#0EA5E9]/20 mb-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-[#0EA5E9]">Enterprise Training Solutions</h3>
                <p className="text-gray-700 mb-4">
                  For larger organizations, we offer comprehensive training programs that can be deployed across departments or company-wide.
                </p>
                <ul className="space-y-3">
                  {["Train-the-trainer programs to build internal AI expertise", "Department-specific customization", "Executive briefings on AI strategy and governance", "Change management support for AI adoption"].map((item, index) => <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#0EA5E9] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>)}
                </ul>
              </div>
              <img alt="Enterprise Training Session" className="w-full h-auto rounded-lg" src="https://www.adaptiveus.com/hubfs/RE.jpg" />
            </div>
          </div>

          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-6 text-[#0EA5E9]">Ready to empower your team with AI?</h2>
            <p className="text-gray-700 mb-8">
              Contact us to discuss your training needs and how we can help your organization effectively harness the power of AI.
            </p>
            <div className="flex justify-center">
              <a href="/contact" className="bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white px-8 py-3 rounded-full font-medium hover:opacity-90 transition-opacity">
                Schedule a Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>;
};
export default AITraining;